import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground bg-card",
        blue: "border-transparent text-blue-950 dark:text-blue-50 bg-blue-200 dark:bg-blue-800 border-blue-300 dark:border-blue-950",
        yellow:
          "border-transparent text-yellow-950 dark:text-yellow-50 bg-yellow-200 dark:bg-yellow-800 border-yellow-300 dark:border-yellow-950",
        pink: "border-transparent text-pink-950 dark:text-pink-50 bg-pink-200 dark:bg-pink-800 border-pink-300 dark:border-pink-950",
        fuchsia:
          "border-transparent text-fuchsia-950 dark:text-fuchsia-50 bg-fuchsia-200 dark:bg-fuchsia-800 border-fuchsia-300 dark:border-fuchsia-950",
        indigo:
          "border-transparent text-indigo-950 dark:text-indigo-50 bg-indigo-200 dark:bg-indigo-800 border-indigo-300 dark:border-indigo-950",
        rose: "border-transparent text-rose-950 dark:text-rose-50 bg-rose-200 dark:bg-rose-800 border-rose-300 dark:border-rose-950",
        emerald:
          "border-transparent text-emerald-950 dark:text-emerald-50 bg-emerald-200 dark:bg-emerald-800 border-emerald-300 dark:border-emerald-950",
        cyan: "border-transparent text-cyan-950 dark:text-cyan-50 bg-cyan-200 dark:bg-cyan-800 border-cyan-300 dark:border-cyan-950",
        lime: "border-transparent text-lime-950 dark:text-lime-50 bg-lime-200 dark:bg-lime-800 border-lime-300 dark:border-lime-950",
        amber:
          "border-transparent text-amber-950 dark:text-amber-50 bg-amber-200 dark:bg-amber-800 border-amber-300 dark:border-amber-950",
        orange:
          "border-transparent text-orange-950 dark:text-orange-50 bg-orange-200 dark:bg-orange-800 border-orange-300 dark:border-orange-950",
        sky: "border-transparent text-sky-950 dark:text-sky-50 bg-sky-200 dark:bg-sky-800 border-sky-300 dark:border-sky-950",
        violet:
          "border-transparent text-violet-950 dark:text-violet-50 bg-violet-200 dark:bg-violet-800 border-violet-300 dark:border-violet-950",
        slate:
          "border-transparent text-slate-950 dark:text-slate-50 bg-slate-200 dark:bg-slate-800 border-slate-300 dark:border-slate-950",
        zinc: "border-transparent text-zinc-950 dark:text-zinc-50 bg-zinc-200 dark:bg-zinc-800 border-zinc-300 dark:border-zinc-950",
      },
      size: {
        default: "text-xs",
        sm: "text-xs px-1.5 py-0.25",
        md: "text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  icon?: React.ReactElement<{ className: string }>;
}

function Badge({ className, variant, size, icon, ...props }: BadgeProps) {
  const badgeIcon = icon
    ? React.cloneElement(icon, {
        className: "text-muted-foreground mr-1 h-3 w-3",
      })
    : null;

  return (
    <div className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {badgeIcon}
      {props.children}
    </div>
  );
}

export { Badge, badgeVariants };
